import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Building2, Car, Users, MapPin, Globe, Calendar, Loader2, AlertCircle } from 'lucide-react';
import { api } from '../services/api';

export default function CompanyPublicProfile() {
  const { companyId } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/public/company/${companyId}`)
      .then(res => setProfile(res.data))
      .catch(err => setError(err.response?.data?.detail || 'Unternehmen nicht gefunden'))
      .finally(() => setLoading(false));
  }, [companyId]);

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Loader2 size={36} className="text-orange-500 animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="text-center bg-white rounded-2xl p-8 max-w-md w-full border border-slate-100">
        <AlertCircle size={40} className="mx-auto text-red-400 mb-3" />
        <h2 className="text-lg font-bold text-slate-800 mb-2">Fehler</h2>
        <p className="text-slate-500 mb-4">{error}</p>
        <a href="/" className="text-orange-500 hover:underline text-sm">Zur Startseite</a>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="company-public-profile">
      <nav className="bg-white border-b border-slate-100">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <a href="/" className="flex items-baseline gap-0.5">
            <span className="text-xl font-extrabold text-orange-500">366</span>
            <span className="text-xs font-semibold text-slate-800">Transfer</span>
          </a>
          <span className="text-sm text-slate-500">Unternehmensprofil</span>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 mb-6">
          <div className="flex items-center gap-5">
            <div className="w-20 h-20 rounded-2xl bg-slate-800 flex items-center justify-center text-white">
              <Building2 size={32} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800" data-testid="company-name">{profile.company_name}</h1>
              <p className="text-sm text-slate-500 mt-1">Ansprechpartner: {profile.contact_person}</p>
              <div className="flex flex-wrap gap-2 mt-3">
                <span className="flex items-center gap-1 px-2.5 py-1 bg-slate-100 rounded-lg text-xs text-slate-600"><MapPin size={12} /> {profile.region}</span>
                <span className="flex items-center gap-1 px-2.5 py-1 bg-slate-100 rounded-lg text-xs text-slate-600"><Car size={12} /> {profile.vehicle_count} Fahrzeuge</span>
                <span className="flex items-center gap-1 px-2.5 py-1 bg-slate-100 rounded-lg text-xs text-slate-600"><Users size={12} /> {profile.driver_count} Fahrer</span>
                {profile.languages?.length > 0 && (
                  <span className="flex items-center gap-1 px-2.5 py-1 bg-slate-100 rounded-lg text-xs text-slate-600"><Globe size={12} /> {profile.languages.join(', ')}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Fleet */}
        {profile.vehicles?.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-100 p-6">
            <h2 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Car size={18} className="text-orange-500" /> Fahrzeugflotte
            </h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {profile.vehicles.map((v, i) => (
                <div key={i} className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                  <p className="font-medium text-slate-800">{v.make} {v.model}</p>
                  <p className="text-sm text-slate-500">{v.color} | {v.plate_number}</p>
                  <div className="flex gap-2 mt-2">
                    <span className="text-xs bg-white px-2 py-0.5 rounded border border-slate-200">{v.vehicle_type}</span>
                    <span className="text-xs bg-white px-2 py-0.5 rounded border border-slate-200">{v.passengers} Passagiere</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
